import { AppIcon } from '@/components/ui/app-icon';
import { ListRowsLink } from '@/components/agent-chat/list-rows-link.component';
import {
  ENRICHMENT_SUBJECT,
  resultRowsFilter,
  runRowView,
  skillSubjectOf,
  textOf,
  type RunProgress,
  type RunSubject,
} from '@/components/agent-chat/run-progress/run-progress.util';
import {
  useEnrichmentRunProgress,
  useSkillRunProgress,
} from '@/components/agent-chat/run-progress/use-run-progress.hook';
import { useNow } from '@/components/agent-chat/use-now.hook';
import {
  iconCircle,
  iconCircleCheck,
  iconSpinnerThird,
  iconTriangleExclamation,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Fragment, type ReactNode } from 'react';

const AMBER = 'text-amber-600 dark:text-amber-400';

const TONE_ICON = {
  running: iconSpinnerThird,
  neutral: iconCircle,
  done: iconCircleCheck,
  failed: iconTriangleExclamation,
} as const;

/**
 * A run as one row, the size of a step row (a 20px icon cell, the label at
 * 12/16 500, a muted caption under it): running, a spinner, "Checking 10
 * people" and "5 of 10 · 18s"; done, "Checked 10 people" and how long it
 * took, with the failed count in amber when some rows failed; and a link to
 * the list, which once a skill's results are in shows the rows it wrote. A
 * run whose task can't be read says only what it is ("Employment check on
 * 10 people") and opens the list: no count, no clock, no end.
 */
export function RunRow({
  subject,
  progress,
  listId,
}: {
  subject: RunSubject;
  progress: RunProgress;
  listId: string | undefined;
}) {
  const live = progress.state === 'running';
  const now = useNow(live);
  const view = runRowView(subject, progress, now);
  const rowsFilter =
    view.tone === 'done' ? resultRowsFilter(subject) : undefined;

  const parts: ReactNode[] = view.caption.map((part) => part);
  if (view.failed) {
    parts.push(
      <span key='failed' className={cn('font-medium', AMBER)}>
        {view.failed}
      </span>
    );
  }
  if (listId) {
    parts.push(
      <ListRowsLink key='link' listId={listId} filters={rowsFilter}>
        {rowsFilter ? 'View results' : 'Open list'}
      </ListRowsLink>
    );
  }

  return (
    <div
      data-run-row
      data-run-tone={view.tone}
      // -2px: its icon lines up with the step rows' above it.
      className='-ml-[2px] flex w-full min-w-0 flex-col items-start gap-[2px] text-[12px] leading-[16px] text-muted-foreground'
    >
      <div className='flex max-w-full items-center gap-[4px]'>
        <span
          className={cn(
            'grid size-[20px] shrink-0 place-items-center',
            view.tone === 'failed' && AMBER
          )}
        >
          <AppIcon
            icon={TONE_ICON[view.tone]}
            spin={view.tone === 'running'}
            style={{ fontSize: 12 }}
          />
        </span>
        <span className='min-w-0 truncate font-medium text-foreground/80'>
          {view.label}
        </span>
      </div>
      {parts.length > 0 ? (
        <div className='flex max-w-full min-w-0 flex-wrap items-center pl-[26px] tabular-nums'>
          {parts.map((part, index) => (
            <Fragment key={index}>
              {index > 0 ? (
                <span aria-hidden='true' className='px-[4px]'>
                  ·
                </span>
              ) : null}
              {part}
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** A skill or AI column run Clu started (`smart_column_started`). */
export function SkillRunRow({ result }: { result: Record<string, unknown> }) {
  const subject = skillSubjectOf(result);
  const progress = useSkillRunProgress(result, subject.name);
  return (
    <RunRow
      subject={subject}
      progress={progress}
      listId={textOf(result.list_id)}
    />
  );
}

/** An enrichment Clu started (`enrichment_started`). */
export function EnrichmentRunRow({
  result,
}: {
  result: Record<string, unknown>;
}) {
  const progress = useEnrichmentRunProgress(result, ENRICHMENT_SUBJECT.name);
  return (
    <RunRow
      subject={ENRICHMENT_SUBJECT}
      progress={progress}
      listId={textOf(result.list_id)}
    />
  );
}
